import { Copy, FileExcel, UploadOne } from '@icon-park/react'

import { Flex, Tooltip } from '@/components'

import { Tokens } from '@/assets/tokens'
import { useRouting } from '@/hooks'
import { Alert, Auth } from '@/modules'
import { useBaseAPI } from '@/modules/api/baseApi.hook'
import { PostgreSQLInterval, UploadStatus, UserResponseRole } from '@shared/models'
import { Component, DownloadButton, FileName, ProgressBar, SelectionCheckbox, TitleSection } from './Card.styles'

const formatDate = (date?: Date): string => {
  if (!date) return 'N/A'
  const d = new Date(date)
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  const seconds = String(d.getSeconds()).padStart(2, '0')
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
}

const formatDuration = (executionTime?: PostgreSQLInterval): string => {
  if (!executionTime) return 'N/A'

  const years = executionTime.years || 0
  const months = executionTime.months || 0
  const days = executionTime.days || 0
  const hours = executionTime.hours || 0
  const totalHours = years * 365 * 24 + months * 30 * 24 + days * 24 + hours
  const minutes = executionTime.minutes || 0
  const secs = Math.floor(executionTime.seconds || 0)

  return `${String(totalHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

export interface UploadItem {
  id?: string
  originalFileName: string
  percentage: number
  status: UploadStatus
  metadata?: string
  createdAt?: Date
  taskId?: string
  executionTime?: PostgreSQLInterval
}

interface CardProps {
  item: UploadItem
  isSelected: boolean
  isSelectionModeActive: boolean
  onToggleSelection: () => void
}

export function Card({ item, isSelected, isSelectionModeActive, onToggleSelection }: CardProps) {
  const { companyID } = useRouting()
  const { sendAlert } = Alert.Hook()
  const { hasAnyRole } = Auth.Hook()
  const isAdmin = hasAnyRole(UserResponseRole.Admin, UserResponseRole.AdminMaster)

  const { instance } = useBaseAPI()

  const handleDownload = async () => {
    try {
      const response = await instance.get(`companies/${companyID}/spreadsheets/${item.id}/logs`, {
        responseType: 'blob',
      })

      sendAlert({
        type: 'success',
        title: 'Sucesso',
        message: 'Dados exportados com sucesso!',
      })

      /* Em prod a API responde { url } com a URL assinada do S3; em local ela
         devolve o próprio .xlsx, então o tipo do conteúdo decide o caminho. */
      const contentType = String(response.headers['content-type'] ?? '')

      if (contentType.includes('application/json')) {
        const { url } = JSON.parse(await (response.data as Blob).text())
        if (!url) return
        window.location.href = url
        return
      }

      const url = URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.download = `erros-${item.originalFileName}.xlsx`
      link.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      sendAlert({
        type: 'error',
        title: 'Erro',
        message: 'Erro ao exportar dados.',
      })
    }
  }

  const handleCopyTaskId = async () => {
    if (!item.taskId) return

    try {
      await navigator.clipboard.writeText(item.taskId)
      sendAlert({
        type: 'success',
        title: 'Copiado!',
        message: 'Task ID copiado para a área de transferência.',
      })
    } catch (error) {
      sendAlert({
        type: 'error',
        title: 'Erro',
        message: 'Não foi possível copiar o Task ID.',
      })
    }
  }

  return (
    <Component>
      {isSelectionModeActive && (
        <SelectionCheckbox>
          <input type="checkbox" checked={isSelected} onChange={onToggleSelection} />
        </SelectionCheckbox>
      )}
      <TitleSection title={item.originalFileName}>
        <Flex $type="row" $justify="flex-start" $align="flex-start" $gap={5} style={{ flexWrap: 'wrap' }}>
          <UploadOne size={20} />
          <FileName>{item.originalFileName}</FileName>
          <Flex $type="row" $justify="flex-end" $align="center" $gap={8} style={{ marginLeft: 'auto' }}>
            {item.status === UploadStatus.FINISHED_WITH_WARNINGS && (
              <DownloadButton onClick={handleDownload} title="Baixar Avisos">
                <FileExcel size={20} fill={Tokens.ColorUIWarnDim} />
              </DownloadButton>
            )}
            {isAdmin && item.taskId && (
              <DownloadButton onClick={handleCopyTaskId} title="Copiar Task ID">
                <Copy size={16} fill={Tokens.ColorTextSecondary} />
              </DownloadButton>
            )}
          </Flex>
        </Flex>
      </TitleSection>
      <Flex $type="column" $justify="space-between" $align="stretch">
        <Flex $type="row" $justify="space-between" $align="center">
          <span>{item.status}</span>
          {(item.status === UploadStatus.ERRORED || item.status === UploadStatus.INVALID_TEMPLATE) && (
            <Tooltip value={item.metadata ?? ''} />
          )}
          <span
            style={{
              fontSize: '20px',
              fontWeight: 600,
              color: Tokens.ColorTextPrimary,
            }}>
            {item.status !== UploadStatus.ERRORED && item.status !== UploadStatus.INVALID_TEMPLATE ?
              `${item.percentage}%`
            : '100%'}
          </span>
        </Flex>
        <ProgressBar
          $percentage={item.percentage}
          $color={
            [UploadStatus.UPLOADING, UploadStatus.UPLOADED, UploadStatus.STARTED].includes(item.status) ?
              Tokens.ColorUIWarn
            : item.status === UploadStatus.FINISHED ?
              Tokens.ColorUIPrimary
            : item.status === UploadStatus.FINISHED_WITH_WARNINGS ?
              Tokens.ColorUIWarnDark
            : item.status === UploadStatus.INVALID_TEMPLATE ?
              Tokens.ColorUIPurple
            : Tokens.ColorUIDanger
          }
        />
        {item.createdAt && (
          <Flex $type="column" $justify="flex-start" $align="stretch" $gap={4} style={{ marginTop: '0.5rem' }}>
            <span style={{ fontSize: '12px', color: Tokens.ColorTextSecondary }}>
              Iniciado em: {formatDate(item.createdAt)}
            </span>
            {item.executionTime !== undefined && (
              <span style={{ fontSize: '12px', color: Tokens.ColorTextSecondary }}>
                Duração do processamento: {formatDuration(item.executionTime)}
              </span>
            )}
          </Flex>
        )}
      </Flex>
    </Component>
  )
}
